import React, { useState } from 'react';
import { supabase } from '../supabaseClient';

const Recruitment = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    preferred_contact: '',
    time_frame: '',
    country_of_origin: '',
    position_applying_for: '',
    job_environment_description: '',
    resume_url: '',
    subscribe_blog: false
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [resumeFile, setResumeFile] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      // Check file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        alert('Only PDF and Word documents are allowed');
        return;
      }
      setResumeFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // For now, we'll store the filename. In production, you'd upload to Supabase Storage
      const dataToSubmit = {
        ...formData,
        resume_filename: resumeFile ? resumeFile.name : null,
        created_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('recruitment')
        .insert([dataToSubmit]);

      if (error) throw error;

      setSubmitted(true);
    } catch (error) {
      console.error('Error:', error);
      setError('There was an error submitting your application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="text-6xl mb-4">✅</div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Application Submitted!</h2>
          <p className="text-gray-600 mb-6">
            Thank you for your interest in joining Plexgate! We've received your application and will review it carefully. 
            Our recruitment team will contact you within 5-7 business days if your profile matches our current opportunities.
          </p>
          <a
            href="/"
            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Return to Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="recruitment-page">
      {/* Hero Section */}
      <section 
        className="py-20 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 relative overflow-hidden"
      >
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920" 
            alt="Team" 
            className="w-full h-full object-cover"
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 relative z-10 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full mb-6">
            <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            IT STAFFING AND RECRUITMENT SERVICES
          </h1>
          
          <p className="text-xl text-white mb-8 max-w-3xl mx-auto">
            Plexgate onboards the right Tech Talent for your teams and projects at the right time.
          </p>
          
          <div className="inline-block bg-white px-8 py-3 rounded-lg">
            <p className="text-blue-700 font-semibold">
              Connect with Plexgate for Professional Staffing Services and Solutions!
            </p>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div className="bg-gray-50 rounded-2xl p-8 border-l-4 border-blue-600">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">2 YEARS OF STAFFING EXPERTISE</h3>
              <p className="text-gray-700">
                Possessing 2 years of dedicated experience in strategically sourcing IT talents, Plexgate practices the best practices in mapping the requirements and culture of our clients with the candidates' skills, ensuring perfect placement.
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-2xl p-8 border-l-4 border-blue-600">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">CUSTOM STAFFING SOLUTIONS</h3>
              <p className="text-gray-700">
                Plexgate identifies the skills that your organization needs and provides the best staffing solutions, including RPO, direct hiring, permanent hiring, contingent staffing, etc., to help you build the right team and operate more efficiently.
              </p>
            </div>
          </div>

          {/* Image Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=800&q=80"
                alt="IT Staffing professionals"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-blue-600 text-white p-6 rounded-xl shadow-xl">
                <p className="text-4xl font-bold">2+</p>
                <p className="text-sm">Years Experience</p>
              </div>
            </div>
            
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                IT Staffing by a Technology Company
              </h2>
              <p className="text-gray-700 mb-4">
                Plexgate is a leading IT Staffing and IT Recruitment Company, headquartered in London, Ontario. Since its incorporation in 2024, Plexgate has been committed to delivering the best staffing solutions for our clients' business needs.
              </p>
              <p className="text-gray-700 mb-6">
                Not only does Plexgate operate as an IT staffing business, but we also offer a complete IT services provider. This provides you with the benefit of a broader, more dependable pool of highly skilled IT consultants that other IT staffing companies simply cannot provide.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">Strategic sourcing of top IT talent</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">Comprehensive staffing solutions</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">Perfect candidate-culture matching</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="bg-gradient-to-br from-blue-50 to-blue-100 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Top IT Staffing Agency Find Niche Technology Skills Faster
            </h2>
            <p className="text-gray-700 max-w-4xl mx-auto">
              If you are looking to hire contract-based, project-based, permanent, or direct hires, Plexgate stands out as the best IT staffing agency. We provide a range of staffing solutions tailored for enterprises of all sizes across diversified industries.
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {[
              {
                title: 'Contract-to-Hire Solution',
                description: 'Evaluate candidates in real work environments before making a permanent hiring decision. Perfect for reducing hiring risks.'
              },
              {
                title: 'Managed Service Provider',
                description: 'Complete workforce management solutions including vendor management, compliance, and strategic planning.'
              },
              {
                title: 'Recruitment Process Outsourcing (RPO)',
                description: 'End-to-end recruitment solutions customized to your organization\'s unique hiring needs and culture.'
              },
              {
                title: 'Permanent and Direct Hiring Solutions',
                description: 'Find the perfect full-time employees for your organization with our comprehensive screening and placement process.'
              },
              {
                title: 'Hybrid Sourcing Solutions',
                description: 'Flexible staffing strategies combining multiple hiring models to meet your changing business needs.'
              }
            ].map((service, index) => (
              <details key={index} className="bg-white rounded-xl shadow-md overflow-hidden group">
                <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors flex items-center justify-between">
                  <span className="font-bold text-gray-900 text-lg">{service.title}</span>
                  <svg className="w-6 h-6 text-blue-600 transform group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                  <p className="text-gray-700">{service.description}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                How Can You Benefit from Our Staffing Services
              </h2>
              <p className="text-gray-700 mb-6">
                We believe that the perfect combination of talents and targets helps organizations achieve amazing things. Plexgate's profound expertise in staffing and recruiting and extensive knowledge in providing strategic and cost-effective contract-to-hire staffing solutions, permanent staffing solutions, and contingent workforce solutions made us the best IT staffing company in the industry.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 rounded-full p-3">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Reduced Time-to-Hire</h4>
                    <p className="text-gray-600">Access to pre-screened, qualified candidates ready to start immediately</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 rounded-full p-3">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Cost Efficiency</h4>
                    <p className="text-gray-600">Reduce recruitment costs with our proven hiring process and candidate pipeline</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 rounded-full p-3">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Flexible Scaling</h4>
                    <p className="text-gray-600">Scale your workforce up or down based on project needs and business cycles</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80"
                alt="Team collaboration"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Submit Resume Section */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-700 py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Submit A Resume
            </h2>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="mb-8">
              <p className="text-gray-700 mb-4">
                That's right, send us your resume and we'll do the work for you! We get it. You're busy making miracles happen and may not have the time to scroll through all of our amazing IT job openings. At the least just attach your resume and send it over – we'll follow up with you. If you've got time, answer a few of our questions.
              </p>
              <p className="text-gray-700">
                Don't worry, unlike other IT jobs sites we won't bombard you with spam or useless opportunities that wastes both of our time. In fact, we consider you more like family, our Plexgate Family, and we like to take care of our family. The only communication you'll get from us is an introduction from your dedicated job specialist and if any jobs arise that meet your specific job desires we'll let you know.
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
                <p className="text-red-700">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* First Name */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Email */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Phone Number */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Preferred Contact Method */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Preferred Way to Reach You
                  </label>
                  <select
                    name="preferred_contact"
                    value={formData.preferred_contact}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">- Please Select -</option>
                    <option value="Email">Email</option>
                    <option value="Phone">Phone</option>
                    <option value="Either">Either</option>
                  </select>
                </div>

                {/* Time Frame */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Time Frame
                  </label>
                  <select
                    name="time_frame"
                    value={formData.time_frame}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">- Please Select -</option>
                    <option value="Immediately">Immediately</option>
                    <option value="Within 2 weeks">Within 2 weeks</option>
                    <option value="Within 1 month">Within 1 month</option>
                    <option value="Just exploring">Just exploring</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Country of Origin */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Country of Origin <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="country_of_origin"
                    value={formData.country_of_origin}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">- Please Select -</option>
                    <option value="Canada">Canada</option>
                    <option value="United States">United States</option>
                    <option value="Mexico">Mexico</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Resume Upload */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Attach Resume
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-sm text-gray-500 mt-1">PDF or Word document (Max 5MB)</p>
                </div>
              </div>

              {/* Position Applying For */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Applying for position <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="position_applying_for"
                  value={formData.position_applying_for}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Full Stack Developer, DevOps Engineer, etc."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Job Environment Description */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Describe the Perfect Job & Company Environment
                </label>
                <textarea
                  name="job_environment_description"
                  value={formData.job_environment_description}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Tell us about your ideal work environment, team culture, and what you're looking for in your next role..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Subscribe Checkbox */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  name="subscribe_blog"
                  checked={formData.subscribe_blog}
                  onChange={handleChange}
                  className="mt-1 w-4 h-4 text-blue-600 rounded"
                />
                <label className="text-gray-700">
                  Subscribe to Our Tech Blog for industry insights, career tips, and technology trends
                </label>
              </div>

              {/* Submit Button */}
              <div className="text-center">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 text-white px-12 py-4 rounded-lg hover:bg-blue-700 transition-colors font-bold text-lg disabled:bg-gray-400 disabled:cursor-not-allowed shadow-lg"
                >
                  {loading ? 'SUBMITTING...' : 'SEND RESUME'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Recruitment;